import dayjs from 'dayjs'
import { Settings as SettingsIcon } from 'lucide-react'
import PropTypes from 'prop-types'
import { Link } from 'react-router'

import { Avatar, AvatarFallback } from '@/components/elements/avatar'
import { Badge } from '@/components/elements/badge'
import { Button } from '@/components/elements/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/elements/card'
import { formatPhoneNumber } from '@/utils/formatters'
import { formatUserInitials, formatUserName } from '@/utils/userHelpers'

const formatDateTime = (value) => (value ? dayjs(value).format('MMM D, YYYY h:mm A') : '—')
const formatDate = (value) => (value ? dayjs(value).format('MMM D, YYYY') : '—')

const Field = ({ label, children }) => (
  <div className="flex flex-col gap-1">
    <span className="text-muted-foreground text-xs tracking-wide uppercase">{label}</span>
    <span className="text-sm">{children || '—'}</span>
  </div>
)

Field.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node,
}

const GeneralSection = ({ entityRole, isSelf }) => {
  const { user, roles = [], jobs = [] } = entityRole
  const accounts = user.accounts || []

  return (
    <Card>
      <CardHeader>
        <div className="flex w-full flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            <Avatar className="size-14">
              <AvatarFallback className="text-base font-semibold">
                {formatUserInitials(user)}
              </AvatarFallback>
            </Avatar>
            <div className="flex flex-col">
              <CardTitle>{formatUserName(user)}</CardTitle>
              <span className="text-muted-foreground text-sm">{user.email}</span>
              {user.title && <span className="text-muted-foreground text-sm">{user.title}</span>}
            </div>
          </div>
          {isSelf && (
            <Button asChild variant="outline" size="sm">
              <Link to="/profile">
                <SettingsIcon className="mr-1 h-4 w-4" />
                Account Settings
              </Link>
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="Phone">{formatPhoneNumber(user.phoneNumber) || '—'}</Field>
          <Field label="MFA Device">{user.mfaDevice}</Field>
          <Field label="Account Status">
            {user.isActive ? (
              <Badge variant="success">Active</Badge>
            ) : (
              <Badge variant="destructive">Inactive</Badge>
            )}
          </Field>
          <Field label="Roles">
            <div className="flex flex-wrap gap-1">
              {roles.length > 0 ? (
                roles.map((role) => (
                  <Badge key={role} variant="secondary">
                    {role}
                  </Badge>
                ))
              ) : (
                <span>—</span>
              )}
            </div>
          </Field>
          <Field label="Jobs">
            <div className="flex flex-wrap gap-1">
              {jobs.length > 0 ? (
                jobs.map((job) => (
                  <Badge key={job.id} variant="outline">
                    {job.title}
                  </Badge>
                ))
              ) : (
                <span>—</span>
              )}
            </div>
          </Field>
          {(user.isXverseAdmin || user.isXverseInstructor || user.isXverseInstructorCandidate) && (
            <Field label="Xverse Roles">
              <div className="flex flex-wrap gap-1">
                {user.isXverseAdmin && <Badge>Xverse Admin</Badge>}
                {user.isXverseInstructor && <Badge>Xverse Instructor</Badge>}
                {user.isXverseInstructorCandidate && <Badge>Xverse Instructor Candidate</Badge>}
              </div>
            </Field>
          )}
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Field label="Joined">{formatDate(user.dateJoined)}</Field>
          <Field label="Last Login">{formatDateTime(user.lastLogin)}</Field>
          <Field label="Last Activity">{formatDateTime(user.lastActivity)}</Field>
        </div>

        {accounts.length > 0 && (
          <div className="flex flex-col gap-2">
            <span className="text-muted-foreground text-xs tracking-wide uppercase">
              Accounts ({accounts.length})
            </span>
            <ul className="divide-accent flex flex-col divide-y rounded-lg border-1">
              {accounts.map((account) => (
                <li
                  key={account.roleId}
                  className="flex flex-row flex-wrap items-center justify-between gap-3 px-4 py-2 text-sm"
                >
                  <span className="font-medium">{account.entityName}</span>
                  <div className="flex flex-wrap gap-1">
                    {(account.roles || []).map((r) => (
                      <Badge key={r} variant="outline">
                        {r}
                      </Badge>
                    ))}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

GeneralSection.propTypes = {
  entityRole: PropTypes.shape({
    roles: PropTypes.arrayOf(PropTypes.string),
    jobs: PropTypes.arrayOf(PropTypes.object),
    user: PropTypes.object.isRequired,
  }).isRequired,
  isSelf: PropTypes.bool,
}

export default GeneralSection
